import { useState } from 'react';
import { Button } from '~/components/ui/button';
import { useCreatePaymentMethodUpdateLink } from '~/pages/FamilyDetail/useSubscriptionAdminActions';

interface AddPaymentMethodButtonProps {
  familyEnrollmentId: string;
  /** Where Stripe sends the parent back to; this page by default. */
  returnUrl?: string;
  onRedirect?: (url: string) => void;
  variant?: 'default' | 'outline' | 'secondary' | 'ghost' | 'link' | 'destructive';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
}

// One button, used wherever a parent can add a saved method: the auto-pay card,
// the saved-methods panel and the pay dialog's empty state. Stripe's portal
// collects the method; we only mint the link and send the parent there.
export function AddPaymentMethodButton({ familyEnrollmentId, returnUrl, onRedirect, variant, size, className }: AddPaymentMethodButtonProps) {
  const [createLink, { loading }] = useCreatePaymentMethodUpdateLink();
  const [error, setError] = useState<string | null>(null);

  const redirect = onRedirect ?? ((url: string) => { window.location.href = url; });

  const handleClick = async () => {
    setError(null);
    try {
      const result = await createLink({
        variables: { input: { familyEnrollmentId, returnUrl: returnUrl ?? window.location.href } },
      });
      const payload = result.data?.createPaymentMethodUpdateLink;
      const message = payload?.userErrors?.[0]?.message ?? null;
      const url = payload?.url ?? null;
      if (message || !url) {
        setError(message || 'Could not start adding a payment method. Please try again.');
        return;
      }
      redirect(url);
    } catch {
      setError('Could not start adding a payment method. Please try again.');
    }
  };

  return (
    <div className={className}>
      <Button type="button" variant={variant} size={size} onClick={() => { void handleClick(); }} disabled={loading}>
        {loading ? 'Starting…' : 'Add a payment method'}
      </Button>
      {error ? <p className="mt-2 text-sm text-destructive">{error}</p> : null}
    </div>
  );
}
